import React, { memo, useMemo } from 'react';
import { format, formatISO } from 'date-fns';
import { View, StyleSheet } from 'react-native';
import LinearGradient from 'utils/packages/linearGradient';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import Spacer from 'design-system/Spacer';
import Squircle from 'design-system/Squircle';
import { IconBank } from 'design-system/icons';
import { linearGradientEnd, linearGradientStart, rem } from 'design-system/values';
import { useAppSelector } from 'store/hooks';
import useColors from 'hooks/useColors';
import { stockSplitNameMap } from '../types';
import { corporateEventGradientColorMap } from '../colors';
const CorporateEventRow = ({ event }) => {
    const colors = useColors();
    const date = formatISO(event.date);
    const name = useAppSelector((state) => ('assetId' in event ? state.invest.caches[event.assetId]?.name : undefined));
    const iconUrl = useAppSelector((state) => 'assetId' in event ? state.invest.caches[event.assetId]?.iconUrl : undefined);
    const displayName = useMemo(() => {
        if (event.type === 'HOLIDAY') {
            if (event.open || event.closed) {
                return 'Market partially closed';
            }
            return 'Market closed';
        }
        return name;
    }, [event, name]);
    const description = useMemo(() => {
        switch (event.type) {
            case 'DIVIDEND':
                return 'Dividend • Payment';
            case 'EX_DIVIDEND':
                return 'Dividend • Ex-dividend ';
            case 'HOLIDAY':
                return event.name ?? 'Market holiday';
            case 'LISTING_CHANGE':
                return 'Listing change';
            case 'MERGER':
                return 'Merger';
            case 'SYMBOL_CHANGE':
                return 'Symbol change';
            case 'SPLIT':
                return `${stockSplitNameMap[event.subType]} split`;
            default:
                return '';
        }
    }, [event]);
    return (<View style={styles.container}>
      <View style={styles.dateContainer}>
        <Text Text-14 Secondary>
          {format(date, 'MMM')}
        </Text>
        <Spacer h={4}/>
        <Text Numbers-20>{format(date, 'd')}</Text>
      </View>

      <Spacer w={4}/>

      <LinearGradient end={linearGradientEnd} start={linearGradientStart} style={styles.linearGradient} colors={colors.gradients[corporateEventGradientColorMap[event.type]].colors}/>

      <View style={styles.eventContainer}>
        <Text Text-16 numberOfLines={1}>
          {displayName}
        </Text>
        <Text TextThin-14 Secondary numberOfLines={1}>
          {description}
        </Text>
      </View>

      <View style={styles.logoContainer}>
        {event.type === 'HOLIDAY' ? (<Squircle color={colors.status.negativeLight}>
            <IconBank colorKey="negative"/>
          </Squircle>) : (<Logo R44 uri={iconUrl} resizeMode="contain"/>)}
      </View>
    </View>);
};
export default memo(CorporateEventRow);
const styles = StyleSheet.create({
    container: {
        padding: rem(16),
        flexDirection: 'row',
    },
    dateContainer: {
        width: rem(56),
        alignItems: 'center',
        justifyContent: 'center',
    },
    linearGradient: {
        width: rem(2),
        borderRadius: rem(10),
    },
    eventContainer: {
        flex: 1,
        justifyContent: 'center',
        paddingHorizontal: rem(16),
    },
    logoContainer: {
        justifyContent: 'center',
    },
});
